import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ChevronLeft, ChevronRight, Search, Lock, Wrench, Zap, Globe, Box,
  Upload, File, FileImage, FileText, FileCode, FileSpreadsheet, FileJson,
  FileVideo, FileAudio, FileArchive, Paperclip,
} from 'lucide-react';
import axios from 'axios';
import { listFiles, uploadFile } from '../api/filesApi';

// ── helpers ──────────────────────────────────────────────────────────────────

const SOURCE_ICONS = {
  builtin:  <Zap size={12} />,
  platform: <Globe size={12} />,
  org:      <Box size={12} />,
};

function fileIcon(mimeType = '') {
  if (mimeType.startsWith('image/'))  return <FileImage size={14} style={{ color: '#10b981' }} />;
  if (mimeType.includes('pdf'))        return <FileText size={14} style={{ color: '#ef4444' }} />;
  if (mimeType.startsWith('text/'))    return <FileText size={14} style={{ color: '#6b7280' }} />;
  if (mimeType.includes('json'))       return <FileJson size={14} style={{ color: '#f59e0b' }} />;
  if (mimeType.includes('spreadsheet') || mimeType.includes('csv') || mimeType.includes('excel'))
    return <FileSpreadsheet size={14} style={{ color: '#22c55e' }} />;
  if (mimeType.includes('javascript') || mimeType.includes('python') || mimeType.includes('code'))
    return <FileCode size={14} style={{ color: '#8b5cf6' }} />;
  if (mimeType.startsWith('video/'))   return <FileVideo size={14} style={{ color: '#3b82f6' }} />;
  if (mimeType.startsWith('audio/'))   return <FileAudio size={14} style={{ color: '#ec4899' }} />;
  if (mimeType.includes('zip') || mimeType.includes('tar') || mimeType.includes('archive'))
    return <FileArchive size={14} style={{ color: '#78716c' }} />;
  return <File size={14} style={{ color: '#9ca3af' }} />;
}

function fmtSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── ToolCard ──────────────────────────────────────────────────────────────────

const ToolCard = ({ tool, onDragStart }) => {
  const locked = tool.is_locked || false;
  return (
    <div
      className={`tool-card${locked ? ' tool-card--locked' : ''}`}
      draggable={!locked}
      onDragStart={locked ? undefined : (e) => onDragStart(e, tool)}
      title={locked ? 'Upgrade to use this tool' : tool.description}
    >
      <div className="tool-card-header">
        <span className="tool-card-name">{tool.name}</span>
        <span className="tool-card-source">{SOURCE_ICONS[tool.source] || <Wrench size={12} />}</span>
        {locked && <Lock size={11} className="tool-card-lock" />}
      </div>
      {tool.description && <p className="tool-card-desc">{tool.description}</p>}
    </div>
  );
};

// ── FileCard ──────────────────────────────────────────────────────────────────

const FileCard = ({ file, onDragStart }) => {
  const isImage = file.mime_type?.startsWith('image/');
  const thumbUrl = isImage ? (file.public_url || null) : null;

  return (
    <div
      className={`file-card${thumbUrl ? ' file-card--image' : ''}`}
      draggable
      onDragStart={(e) => onDragStart(e, file)}
      title={`${file.filename}\n${fmtSize(file.file_size)}`}
    >
      {thumbUrl ? (
        <div className="file-card-thumb">
          <img src={thumbUrl} alt={file.filename} className="file-card-thumb-img" />
        </div>
      ) : (
        <span className="file-card-icon">{fileIcon(file.mime_type)}</span>
      )}
      <div className="file-card-info">
        <span className="file-card-name">{file.filename}</span>
        <span className="file-card-meta">{fmtSize(file.file_size)}</span>
      </div>
      <Paperclip size={11} className="file-card-drag-hint" />
    </div>
  );
};

// ── ToolPanel ─────────────────────────────────────────────────────────────────

const ToolPanel = ({ onDragStart, onFileDragStart, isOpen, onToggle, flowId }) => {
  const [tab, setTab] = useState('tools'); // 'tools' | 'files'

  // Tools state
  const [tools, setTools] = useState([]);
  const [toolsLoading, setToolsLoading] = useState(false);
  const [search, setSearch] = useState('');

  // Files state
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // null | 0-100
  const uploadRef = useRef(null);

  // ── fetch tools ────────────────────────────────────────────────────────────
  const fetchTools = useCallback(async () => {
    setToolsLoading(true);
    try {
      const res = await axios.get('/api/tools/catalog', { timeout: 10000 });
      setTools([...(res.data.tools || res.data || [])]);
    } catch (err) {
      console.error('Tool catalog fetch failed:', err);
    } finally {
      setToolsLoading(false);
    }
  }, []);

  // ── fetch files ────────────────────────────────────────────────────────────
  const fetchFiles = useCallback(async () => {
    setFilesLoading(true);
    try {
      const result = await listFiles({ limit: 100, file_type: null });
      setFiles(result.files || result || []);
    } catch (err) {
      console.error('Files fetch failed:', err);
    } finally {
      setFilesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    if (tab === 'tools' && tools.length === 0) fetchTools();
    if (tab === 'files') fetchFiles();
  }, [isOpen, tab, fetchTools, fetchFiles, tools.length]);

  // ── upload handler ─────────────────────────────────────────────────────────
  const handleUploadChange = useCallback(async (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;
    e.target.value = '';
    setUploadProgress(0);
    try {
      const uploaded = await Promise.all(
        selected.map((f) => uploadFile(f, (pct) => setUploadProgress(pct), { workflowId: flowId }))
      );
      setFiles((prev) => [...uploaded, ...prev]);
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setUploadProgress(null);
    }
  }, [flowId]);

  // ── file drag ──────────────────────────────────────────────────────────────
  const handleFileDragStart = useCallback((e, file) => {
    e.dataTransfer.setData('application/flowize-file', JSON.stringify(file));
    e.dataTransfer.effectAllowed = 'copy';
    if (onFileDragStart) onFileDragStart(e, file);
  }, [onFileDragStart]);

  // ── tools tab rendering ────────────────────────────────────────────────────
  const filtered = tools.filter((t) =>
    !search ||
    t.name?.toLowerCase().includes(search.toLowerCase()) ||
    t.description?.toLowerCase().includes(search.toLowerCase())
  );
  const builtins = filtered.filter((t) => t.source === 'builtin');
  const platform = filtered.filter((t) => t.source === 'platform');
  const org = filtered.filter((t) => t.source === 'org' || (!t.source && t.slug));

  return (
    <div className={`tool-panel${isOpen ? '' : ' tool-panel--collapsed'}`}>
      <button
        className="tool-panel-toggle"
        onClick={onToggle}
        title={isOpen ? 'Collapse panel' : 'Expand panel'}
      >
        {isOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
      </button>

      {isOpen && (
        <div className="tool-panel-inner">
          {/* ── tabs ─────────────────────────────────────────────────── */}
          <div className="tool-panel-tabs">
            <button
              className={`tool-panel-tab${tab === 'tools' ? ' tool-panel-tab--active' : ''}`}
              onClick={() => setTab('tools')}
            >
              <Wrench size={12} /> Tools
            </button>
            <button
              className={`tool-panel-tab${tab === 'files' ? ' tool-panel-tab--active' : ''}`}
              onClick={() => setTab('files')}
            >
              <File size={12} /> Files
            </button>
          </div>

          {/* ── tools tab ────────────────────────────────────────────── */}
          {tab === 'tools' && (
            <>
              <div className="tool-panel-search">
                <Search size={13} />
                <input
                  type="text"
                  placeholder="Search tools…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onMouseDown={(e) => e.stopPropagation()}
                />
              </div>
              <div className="tool-panel-list">
                {toolsLoading && <div className="tool-panel-empty">Loading…</div>}
                {!toolsLoading && filtered.length === 0 && (
                  <div className="tool-panel-empty">No tools found</div>
                )}
                {builtins.length > 0 && (
                  <div className="tool-group">
                    <div className="tool-group-label">Built-in</div>
                    {builtins.map((t) => <ToolCard key={t.slug} tool={t} onDragStart={onDragStart} />)}
                  </div>
                )}
                {platform.length > 0 && (
                  <div className="tool-group">
                    <div className="tool-group-label">Platform</div>
                    {platform.map((t) => <ToolCard key={t.slug} tool={t} onDragStart={onDragStart} />)}
                  </div>
                )}
                {org.length > 0 && (
                  <div className="tool-group">
                    <div className="tool-group-label">Your tools</div>
                    {org.map((t) => <ToolCard key={t.slug} tool={t} onDragStart={onDragStart} />)}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── files tab ────────────────────────────────────────────── */}
          {tab === 'files' && (
            <div className="tool-panel-list">
              {/* upload button */}
              <button
                className="file-upload-btn"
                onClick={() => uploadRef.current?.click()}
                disabled={uploadProgress !== null}
              >
                <Upload size={13} />
                {uploadProgress !== null ? `Uploading… ${uploadProgress}%` : 'Upload files'}
              </button>
              <input
                ref={uploadRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={handleUploadChange}
              />

              {filesLoading && <div className="tool-panel-empty">Loading…</div>}
              {!filesLoading && files.length === 0 && (
                <div className="tool-panel-empty">No files yet</div>
              )}
              {files.map((f) => (
                <FileCard key={f.id} file={f} onDragStart={handleFileDragStart} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ToolPanel;
